import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { useLayoutEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import ctaImage from '@/assets/cta-beratung.webp';
import ctaImageMobile from '@/assets/cta-beratung-mobile.png';
const CTASection = () => {
  const ref = useRef(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const headlineRef = useRef<HTMLHeadingElement | null>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [lineHeight, setLineHeight] = useState(160);
  const DOT_SIZE = 12;
  const DOT_RADIUS = DOT_SIZE / 2;

  // Track scroll progress for the timeline endpoint animation
  // The line from ProcessTimelineSection already extends 160px into this section and fills via its own scroll.
  // This CTA progress only drives the LAST segment (from ~160px mark down to the endpoint dot).
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    // Start later (to avoid filling while still in Schritt 3/4), but fast enough that
    // the fill is already visible through the CTA hero area.
    offset: ["start 65%", "start 25%"]
  });
  const ctaProgress = useTransform(scrollYProgress, [0, 1], [0, 1]);

  // Endpoint appears only at the very end of the CTA fill.
  const endpointOpacity = useTransform(ctaProgress, [0.92, 1], [0, 1]);
  const buttonGlow = useTransform(ctaProgress, [0.85, 1], [0, 1]);

  // Position the end point exactly above the CTA headline and align the line end to the dot center
  useLayoutEffect(() => {
    const calc = () => {
      if (!sectionRef.current || !headlineRef.current) return;
      const sectionRect = sectionRef.current.getBoundingClientRect();
      const headlineRect = headlineRef.current.getBoundingClientRect();
      const headlineOffset = headlineRect.top - sectionRect.top;
      // End the dot clearly ABOVE the headline (never inside it), independent of scroll offsets.
      // We anchor the DOT CENTER to a point above the headline top.
      // Increase gap => endpoint sits higher above the headline (as requested)
      const gapAboveHeadline = Math.max(72, Math.min(120, headlineRect.height * 1.35));
      const dotCenterY = headlineOffset - gapAboveHeadline;
      const nextHeight = Math.max(120, dotCenterY + DOT_RADIUS);
      setLineHeight(nextHeight);
    };
    calc();
    const ro = new ResizeObserver(() => calc());
    if (sectionRef.current) ro.observe(sectionRef.current);
    if (headlineRef.current) ro.observe(headlineRef.current);
    window.addEventListener('resize', calc);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', calc);
    };
  }, []);
  return <section ref={sectionRef} className="relative py-32 overflow-hidden">
      {/* Desktop: Timeline continuation - uses same container structure as ProcessTimelineSection for perfect alignment */}
      <div className="hidden md:block absolute inset-x-0 top-0 z-20" style={{ height: `${lineHeight}px` }}>
        <div className="container max-w-[950px] px-6 h-full relative">
          <div className="absolute left-1/2 h-full" style={{ transform: 'translateX(-50%)' }}>
            {/* Background line (grey) */}
            <div className="absolute" style={{
              backgroundColor: 'hsl(220 15% 22%)',
              width: '1px',
              left: '50%',
              transform: 'translateX(-50%)',
              top: -1,
              bottom: 6
            }} />
            
            {/* Progress fill (red) - animates based on scroll */}
            <motion.div className="absolute" style={{
              backgroundColor: 'hsl(var(--primary))',
              width: '1px',
              left: '50%',
              transform: 'translateX(-50%)',
              top: -1,
              bottom: 6,
              scaleY: ctaProgress,
              transformOrigin: 'top center'
            }} />
            
            {/* Final endpoint with glow */}
            <motion.div className="absolute bottom-0 left-1/2 -translate-x-1/2" style={{
              opacity: endpointOpacity
            }}>
              {/* Glow ring */}
              <div className="absolute -inset-3 rounded-full" style={{
                background: 'radial-gradient(circle, hsl(var(--primary) / 0.35) 0%, transparent 70%)'
              }} />
              {/* Main dot */}
              <div className="w-3 h-3 rounded-full relative" style={{
                backgroundColor: 'hsl(var(--primary))',
                boxShadow: '0 0 14px hsl(var(--primary) / 0.55)'
              }} />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Mobile: Timeline continuation - positioned at same distance from viewport edge as ProcessTimelineSection */}
      {/* ProcessTimelineSection uses container px-6 (24px) + left: 7px = 31px from viewport edge */}
      <div
        className="md:hidden absolute z-20"
        style={{ 
          left: '31px',
          top: 0,
          height: `${Math.max(80, lineHeight - 40)}px` 
        }}
      >
        {/* Background line (grey) */}
        <div
          className="absolute"
          style={{
            backgroundColor: 'hsl(220 15% 25%)',
            width: '2px',
            left: 0,
            top: -1,
            bottom: 6,
          }}
        />

        {/* Progress fill (red) - exact same position as background */}
        <motion.div
          className="absolute"
          style={{
            backgroundColor: 'hsl(var(--primary))',
            width: '2px',
            left: 0,
            top: -1,
            bottom: 6,
            scaleY: ctaProgress,
            transformOrigin: 'top',
          }}
        />

        {/* Final endpoint with glow */}
        <motion.div
          className="absolute bottom-0"
          style={{
            left: '1px',
            transform: 'translateX(-50%)',
            opacity: endpointOpacity,
          }}
        >
          {/* Glow ring */}
          <div
            className="absolute -inset-2 rounded-full"
            style={{
              background:
                'radial-gradient(circle, hsl(var(--primary) / 0.3) 0%, transparent 70%)',
            }}
          />
          {/* Main dot */}
          <div
            className="w-2.5 h-2.5 rounded-full relative"
            style={{
              backgroundColor: 'hsl(var(--primary))',
              boxShadow: '0 0 10px hsl(var(--primary) / 0.5)',
            }}
          />
        </motion.div>
      </div>

      {/* Background Image - Desktop */}
      <div className="absolute inset-0 hidden md:block" style={{
      backgroundImage: `url(${ctaImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }} />
      
      {/* Background Image - Mobile: separate optimized image */}
      <div className="absolute inset-0 md:hidden" style={{
      backgroundImage: `url(${ctaImageMobile})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center top'
    }} />
      
      {/* Overlays */}
      <div className="absolute inset-0 bg-foreground/70" />

      <div className="container relative z-10" ref={ref}>
        <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} className="max-w-3xl mx-auto text-center pt-24 md:pt-28">
          {/* Headline */}
          <h2 ref={headlineRef} className="text-3xl md:text-4xl lg:text-5xl font-medium mb-6 text-white font-serif">
            Du bist am Zug.
          </h2>

          <p className="text-xl text-white/80 mb-12 font-light max-w-xl mx-auto">
            Unverbindliches Gespräch vereinbaren und deine finanzielle Zukunft selbst in die Hand nehmen.
          </p>

          {/* CTA Button with optional glow effect */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative inline-block">
            {/* Glow effect behind button */}
            <motion.div className="absolute -inset-4 rounded-xl hidden md:block" style={{
            background: 'radial-gradient(ellipse at center, hsl(var(--primary) / 0.3) 0%, transparent 70%)',
            opacity: buttonGlow
          }} />
            <Link to="/beratung/individuell">
              <Button size="xl" className="relative bg-white text-foreground hover:bg-white/90 font-medium text-base px-10 group animate-glow-pulse">
                Kostenfreies Gespräch buchen
                <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </motion.div>

          {/* Trust Note */}
          <motion.div initial={{
          opacity: 0
        }} animate={isInView ? {
          opacity: 1
        } : {}} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="flex flex-wrap justify-center gap-x-6 gap-y-1 text-sm text-white/60 mt-8">
            <span className="whitespace-nowrap">✓ Unverbindlich</span>
            <span className="whitespace-nowrap">✓ Kostenfrei</span>
            <span className="whitespace-nowrap">✓ Apothekerspezifisch</span>
          </motion.div>
        </motion.div>
      </div>
    </section>;
};
export default CTASection;
